import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Textarea } from '@shared/components/ui/textarea';
import { Badge } from '@shared/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { Switch } from '@shared/components/ui/switch';
import {
  Plus,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  XCircle,
  MapPin,
  Calendar,
  User,
  Image as ImageIcon,
  Eye,
  Edit2,
  MessageSquare,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { useAuth } from '@shared/contexts/AuthContext';
import { useToast } from '@shared/components/ui/use-toast';

/**
 * Incident Reporting
 * Create and manage safety/environmental/security incidents
 */
const IncidentReporting = () => {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const { toast } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'safety',
    severity: 'medium',
    location: '',
    work_area: '',
    occurred_at: new Date().toISOString().split('T')[0] + 'T' + new Date().toISOString().split('T')[1].substring(0, 5),
    people_involved: '',
    is_anonymous: false,
    photos: [],
  });

  useEffect(() => {
    loadIncidents();
  }, []);

  const loadIncidents = async () => {
    if (!user?.depot_id) { setLoading(false); return; }
    
    setLoading(true);
    try {
      let query = supabase
        .from('incidents')
        .select('*')
        .eq('depot_id', user.depot_id)
        .order('created_at', { ascending: false });
      
      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setIncidents(data || []);
    } catch (error) {
      console.error('Error loading incidents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load incidents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIncidents();
  }, [filterStatus]);

  const handleReportIncident = async () => {
    if (!formData.title || !formData.description || !formData.location) {
      toast({
        title: 'Validation Error',
        description: 'Title, description, and location are required',
        variant: 'destructive',
      });
      return;
    }

    try {
      const incidentData = {
        depot_id: user.depot_id,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        severity: formData.severity,
        location: formData.location,
        work_area: formData.work_area || null,
        occurred_at: formData.occurred_at,
        people_involved: formData.people_involved
          ? formData.people_involved.split(',').map(p => p.trim()).filter(p => p)
          : [],
        is_anonymous: formData.is_anonymous,
        reporter_email: formData.is_anonymous ? null : user.email,
        reporter_name: formData.is_anonymous ? 'Anonymous' : `${user.first_name} ${user.surname}`,
        reported_by: user.id,
        status: 'open',
        photos: formData.photos || [],
      };

      const { error } = await supabase.from('incidents').insert([incidentData]);
      
      if (error) throw error;

      toast({
        title: 'Incident Reported',
        description: 'Incident has been logged successfully',
      });

      setIsReportModalOpen(false);
      resetForm();
      await loadIncidents();
    } catch (error) {
      console.error('Error reporting incident:', error);
      toast({
        title: 'Error',
        description: 'Failed to report incident',
        variant: 'destructive',
      });
    }
  };

  const handleResolveIncident = async (incident) => {
    const resolution = prompt('Enter resolution notes:');
    if (!resolution) return;

    try {
      const { error } = await supabase
        .from('incidents')
        .update({
          status: 'resolved',
          resolution_notes: resolution,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', incident.id);
      
      if (error) throw error;
      
      toast({
        title: 'Incident Resolved',
        description: 'Incident has been marked as resolved',
      });
      await loadIncidents();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resolve incident',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'safety',
      severity: 'medium',
      location: '',
      work_area: '',
      occurred_at: new Date().toISOString().split('T')[0] + 'T' + new Date().toISOString().split('T')[1].substring(0, 5),
      people_involved: '',
      is_anonymous: false,
      photos: [],
    });
  };

  const getCategoryBadge = (category) => {
    const variants = {
      safety: { bg: 'bg-red-500', icon: AlertTriangle },
      environmental: { bg: 'bg-green-500', icon: AlertCircle },
      security: { bg: 'bg-blue-500', icon: AlertCircle },
      quality: { bg: 'bg-purple-500', icon: AlertCircle },
      other: { bg: 'bg-gray-500', icon: AlertCircle },
    };
    const variant = variants[category] || variants.other;
    const Icon = variant.icon;
    return (
      <Badge className={`${variant.bg} text-white`}>
        <Icon className="h-3 w-3 mr-1" />
        {category}
      </Badge>
    );
  };

  const getSeverityBadge = (severity) => {
    const variants = {
      low: 'bg-yellow-500',
      medium: 'bg-orange-500',
      high: 'bg-red-500',
      critical: 'bg-red-700',
    };
    return <Badge className={`${variants[severity] || variants.medium} text-white`}>{severity}</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: { bg: 'bg-orange-500', text: 'Open' },
      investigating: { bg: 'bg-blue-500', text: 'Investigating' },
      resolved: { bg: 'bg-green-500', text: 'Resolved' },
      closed: { bg: 'bg-gray-500', text: 'Closed' },
    };
    const variant = variants[status] || variants.open;
    return <Badge className={`${variant.bg} text-white`}>{variant.text}</Badge>;
  };

  const stats = {
    total: incidents.length,
    open: incidents.filter(i => i.status === 'open').length,
    investigating: incidents.filter(i => i.status === 'investigating').length,
    resolved: incidents.filter(i => i.status === 'resolved').length,
  };

  if (!user?.depot_id && !loading) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center max-w-sm">
          <p className="text-lg font-semibold text-foreground">No site assigned</p>
          <p className="mt-2 text-sm text-muted-foreground">Your account is not yet linked to a site. Contact your system administrator to be assigned to a depot.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading incidents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Incident Reporting</h1>
          <p className="text-muted-foreground mt-1">
            Report and track safety, environmental, and security incidents
          </p>
        </div>
        <Button onClick={() => setIsReportModalOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Report Incident
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Incidents</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open</p>
                <p className="text-2xl font-bold text-orange-500">{stats.open}</p>
              </div>
              <XCircle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Investigating</p>
                <p className="text-2xl font-bold text-blue-500">{stats.investigating}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolved</p>
                <p className="text-2xl font-bold text-green-500">{stats.resolved}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Label>Filter by Status:</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Incidents</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="investigating">Investigating</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Incidents List */}
      {incidents.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No incidents</h3>
              <p className="text-muted-foreground mt-2">
                {filterStatus !== 'all' ? `No ${filterStatus} incidents found.` : 'No incidents have been reported.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {incidents.map((incident) => (
            <Card key={incident.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-semibold">{incident.title}</h3>
                      {getStatusBadge(incident.status)}
                      {getCategoryBadge(incident.category)}
                      {getSeverityBadge(incident.severity)}
                    </div>

                    <p className="text-muted-foreground">{incident.description}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{incident.location}</span>
                        {incident.work_area && ` - ${incident.work_area}`}
                      </div>

                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Occurred: {new Date(incident.occurred_at).toLocaleString()}</span>
                      </div>

                      <div className="flex items-center gap-2 text-muted-foreground">
                        <User className="h-4 w-4" />
                        <span>Reported by: {incident.is_anonymous ? 'Anonymous' : incident.reporter_name}</span>
                      </div>

                      {incident.people_involved && incident.people_involved.length > 0 && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <User className="h-4 w-4" />
                          <span>Involved: {incident.people_involved.join(', ')}</span>
                        </div>
                      )}
                    </div>

                    {incident.status === 'resolved' && incident.resolution_notes && (
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mt-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-green-900 dark:text-green-100">Resolution</p>
                            <p className="text-sm text-green-700 dark:text-green-300">{incident.resolution_notes}</p>
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              Resolved: {new Date(incident.resolved_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedIncident(incident);
                        setIsViewModalOpen(true);
                      }}
                      className="flex items-center gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                    
                    {incident.status === 'open' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResolveIncident(incident)}
                        className="flex items-center gap-2"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Report Incident Modal */}
      <Dialog open={isReportModalOpen} onOpenChange={setIsReportModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Report Incident</DialogTitle>
            <DialogDescription>
              Document a safety, environmental, or security incident
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Incident Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Brief description of incident"
              />
            </div>

            <div>
              <Label htmlFor="description">Full Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed description of what happened..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="safety">Safety</SelectItem>
                    <SelectItem value="environmental">Environmental</SelectItem>
                    <SelectItem value="security">Security</SelectItem>
                    <SelectItem value="quality">Quality</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="severity">Severity *</Label>
                <Select
                  value={formData.severity}
                  onValueChange={(value) => setFormData({ ...formData, severity: value })}
                >
                  <SelectTrigger id="severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Warehouse, Loading Bay, etc."
                />
              </div>

              <div>
                <Label htmlFor="work_area">Work Area</Label>
                <Input
                  id="work_area"
                  value={formData.work_area}
                  onChange={(e) => setFormData({ ...formData, work_area: e.target.value })}
                  placeholder="Specific area or zone"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="occurred_at">When did it occur? *</Label>
              <Input
                id="occurred_at"
                type="datetime-local"
                value={formData.occurred_at}
                onChange={(e) => setFormData({ ...formData, occurred_at: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="people_involved">People Involved (comma-separated)</Label>
              <Input
                id="people_involved"
                value={formData.people_involved}
                onChange={(e) => setFormData({ ...formData, people_involved: e.target.value })}
                placeholder="John Smith, Jane Doe"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="is_anonymous"
                checked={formData.is_anonymous}
                onCheckedChange={(checked) => setFormData({ ...formData, is_anonymous: checked })}
              />
              <Label htmlFor="is_anonymous">Submit anonymously</Label>
            </div>

            <div className="border-t pt-4">
              <Label>Photos (Mock - will support upload in production)</Label>
              <div className="mt-2 p-4 border-2 border-dashed rounded-lg text-center">
                <ImageIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Photo upload coming soon</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReportModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReportIncident}
              disabled={!formData.title || !formData.description || !formData.location}
            >
              Report Incident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Incident Modal */}
      {selectedIncident && (
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedIncident.title}</DialogTitle>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(selectedIncident.status)}
                {getCategoryBadge(selectedIncident.category)}
                {getSeverityBadge(selectedIncident.severity)}
              </div>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Description</Label>
                <p className="mt-1">{selectedIncident.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Location</Label>
                  <p className="mt-1">{selectedIncident.location}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Work Area</Label>
                  <p className="mt-1">{selectedIncident.work_area || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Occurred At</Label>
                  <p className="mt-1">{new Date(selectedIncident.occurred_at).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Reported By</Label>
                  <p className="mt-1">{selectedIncident.is_anonymous ? 'Anonymous' : selectedIncident.reporter_name}</p>
                </div>
              </div>

              {selectedIncident.people_involved && selectedIncident.people_involved.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">People Involved</Label>
                  <p className="mt-1">{selectedIncident.people_involved.join(', ')}</p>
                </div>
              )}

              {selectedIncident.status === 'resolved' && (
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <Label className="text-xs text-muted-foreground">Resolution</Label>
                  <p className="mt-1">{selectedIncident.resolution_notes}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Resolved: {new Date(selectedIncident.resolved_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button onClick={() => setIsViewModalOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default IncidentReporting;
